import { ExternalLink, Gift, MoreVertical, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WishlistItem } from '@/hooks/queries/use-relationship';
import { cn } from '@/lib/utils';

export interface WishlistItemCardProps {
  item: WishlistItem;
  onEdit?: (item: WishlistItem) => void;
  onDelete?: (item: WishlistItem) => void;
  onClick?: (item: WishlistItem) => void;
  readOnly?: boolean;
  variant?: 'default' | 'partner';
}

export function WishlistItemCard({
  item,
  onEdit,
  onDelete,
  onClick,
  readOnly = false,
  variant = 'default',
}: WishlistItemCardProps) {
  const showActions = !readOnly && (onEdit || onDelete);

  return (
    <Card
      className={cn(
        'cursor-pointer transition-shadow hover:shadow-md',
        variant === 'partner' && 'border-violet-200 dark:border-violet-800',
      )}
      onClick={() => onClick?.(item)}
    >
      <div className="bg-muted flex aspect-square items-center justify-center rounded-t-lg">
        <Gift
          className={cn(
            'h-12 w-12',
            variant === 'partner' ? 'text-violet-400' : 'text-muted-foreground',
          )}
        />
      </div>

      <CardContent className="p-3">
        <h3 className="text-foreground truncate font-medium">{item.title}</h3>
        {item.description && (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
            {item.description}
          </p>
        )}

        <div className="mt-2 flex items-center justify-between">
          {item.link ? (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : (
            <div />
          )}

          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">Abrir menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(item);
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
